import { AuthTokens, AuthenticationResult, LoginResponse, Role, User } from '../types'

const TOKEN_KEY = 'templatean_auth_tokens'
const USER_KEY = 'templatean_user'

/** Name of the DOM event dispatched when the session can no longer be refreshed. */
export const AUTH_EXPIRED_EVENT = 'templatean:auth-expired'

export const tokenUtils = {
  getTokens(): AuthTokens | null {
    try {
      const tokens = localStorage.getItem(TOKEN_KEY)
      return tokens ? JSON.parse(tokens) : null
    } catch {
      return null
    }
  },

  setTokens(tokens: AuthTokens): void {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
  },

  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  },

  /** `expiresIn` is an absolute Unix timestamp in seconds. */
  isTokenExpired(expiresIn: number): boolean {
    return Date.now() >= expiresIn * 1000
  },

  getAuthHeader(): string | null {
    const tokens = this.getTokens()
    return tokens ? `${tokens.tokenType} ${tokens.accessToken}` : null
  },
}

export const parseJwt = (token: string): any => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

/**
 * Derive the absolute expiry (Unix seconds) for an access token.
 * Prefers the API-supplied `expiresAt`, falls back to the JWT `exp` claim,
 * and finally to a conservative 15 minute window.
 */
export const resolveTokenExpiry = (accessToken: string, expiresAt?: string | null): number => {
  if (expiresAt) {
    const ms = Date.parse(expiresAt)
    if (!Number.isNaN(ms)) return Math.floor(ms / 1000)
  }
  const claims = parseJwt(accessToken)
  if (claims && typeof claims.exp === 'number') return claims.exp
  return Math.floor(Date.now() / 1000) + 15 * 60
}

/** Convert the API's flat `AuthenticationResult` into the add-in's token shape. */
export const toAuthTokens = (result: AuthenticationResult): AuthTokens => {
  if (!result.success || !result.accessToken || !result.refreshToken) {
    throw new Error(result.errorMessage || 'Authentication failed')
  }
  return {
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    expiresIn: resolveTokenExpiry(result.accessToken, result.expiresAt),
    tokenType: 'Bearer',
  }
}

/** Build the add-in `User` from the API's flat `AuthenticationResult`. */
export const toUser = (result: AuthenticationResult): User => {
  const claims = result.accessToken ? parseJwt(result.accessToken) : null
  const roleNames = result.roles ?? []
  const roles: Role[] = roleNames.map(name => ({ id: name, name, permissions: [] }))
  return {
    id: result.userId ?? claims?.sub ?? '',
    email: result.email ?? claims?.email ?? '',
    fullName: result.fullName ?? claims?.name ?? result.email ?? '',
    tenantId: claims?.tenant_id ?? claims?.tenantId ?? '',
    roles,
    permissions: result.permissions ?? [],
  }
}

/** Map a login/refresh payload into `{ user, tokens }`. */
export const mapAuthResult = (result: AuthenticationResult): LoginResponse => ({
  tokens: toAuthTokens(result),
  user: toUser(result),
})

export const hasPermission = (
  userPermissions: string[],
  requiredPermission: string
): boolean => {
  return userPermissions.includes(requiredPermission) ||
         userPermissions.includes('*') ||
         userPermissions.some(p => {
           const pattern = p.replace('*', '.*')
           return new RegExp(`^${pattern}$`).test(requiredPermission)
         })
}
